/** Numerotarea funcțiunilor unui nivel și legenda care le explică.
 *
 *  MODELUL E AL PLANȘEI DE REFERINȚĂ, nu al unui tabel de camere. Pe planul
 *  vilei Cheshme scrie «1 ENTRANCE, 2 WC, 3 HALL, 4 DINING, 5 KITCHEN» — cinci
 *  numere pentru cinci FUNCȚIUNI, nu pentru cinci încăperi. Diferența contează
 *  aici mai mult decât acolo: un etaj de bloc are 44 de spații și 12 funcțiuni.
 *  Numerotate una câte una, ar ieși o legendă de 44 de rânduri, adică o listă pe
 *  care n-o citește nimeni; numerotate pe funcțiune, ies 12 rânduri și fiecare
 *  număr apare de câte ori apare camera — exact cum se citește un plan.
 *
 *  NUMERELE TREBUIE SĂ FIE STABILE. Aceeași cameră ia același număr la fiecare
 *  randare, altfel legenda se rearanjează când omul stinge un strat, iar un
 *  desen care își schimbă cifrele când atingi altceva nu se poate folosi ca
 *  referință într-o discuție. De asta ordinea NU e cea de iterare a
 *  documentului, ci `ORDINE` de mai jos — o ordine fixă, de la spațiul de zi
 *  spre cel tehnic, aceeași pentru orice nivel al oricărui proiect.
 */
import type { StoreyPlan } from "../canonical";
import { HABITABLE } from "../generated/kinds";
import { t } from "../../i18n";
import type { Cheie } from "../../i18n";

/** Numele scris pe un CORP DE FUNCȚIUNE (școală, birouri, comercial de sine
 *  stătător — `Building.properties.functiune`, format 3.21 §40), pe planșa de
 *  situație și în inspector: scurt, ca «P+1» de sub el, nu titlul bifei din temă.
 *  O funcțiune necunoscută se scrie cum e, nu se ascunde. */
export const NUME_CORP_FUNCTIUNE: Record<string, Cheie> = {
  scoala: "corp.functiune.scoala",
  birouri: "corp.functiune.birouri",
  comercial: "corp.functiune.comercial",
};
export function numeCorpFunctiune(functiune: string): string {
  const k = NUME_CORP_FUNCTIUNE[functiune];
  return k ? t(k) : functiune;
}

/** Numele românesc al funcțiunii, pentru legendă.
 *
 *  Separat de `display.label` al obiectului DINADINS: eticheta unei camere e
 *  „Dormitor 1", „Dormitor 2" — numele instanței. Legenda vorbește despre
 *  funcțiune, deci are nevoie de „Dormitor", la singular și fără număr. */
export const NUME_FUNCTIUNE: Record<string, Cheie> = {
  living: "functiune.living",
  kitchen: "functiune.kitchen",
  bedroom: "functiune.bedroom",
  dormitor: "functiune.dormitor",
  hall: "functiune.hall",
  // `hol` e holul DE NIVEL al nucleului (`CorePart`), nu holul apartamentului
  // (`hall`): două rânduri «Hol» cu numere diferite în aceeași legendă ar fi
  // arătat ca o greșeală.
  hol: "functiune.hol",
  // spațiul administrativ al clădirii (format 3.11): unul pe corp, la parter
  administrativ: "functiune.administrativ",
  // spațiul de servicii de pe parterul comercial (format 3.20): unul pe corp
  comercial: "functiune.comercial",
  // corpurile de funcțiune (format 3.21): un spațiu deschis pe etaj
  birouri: "functiune.birouri",
  invatamant: "functiune.invatamant",
  bath: "functiune.bath",
  bath2: "functiune.bath2",
  shower: "functiune.shower",
  wc: "functiune.wc",
  store: "functiune.store",
  balcony: "functiune.balcony",
  sas: "functiune.sas",
  scara: "functiune.scara",
  lift: "functiune.lift",
  ghena: "functiune.ghena",
  bazin: "functiune.bazin",
  tech: "functiune.tech",
};

/** Ordinea fixă în care se dau numerele: de la ce se locuiește spre ce
 *  deservește. E convenția planșelor de referință — intrarea și zona de zi
 *  întâi — și e singurul lucru care face numerele stabile între niveluri: pe
 *  orice etaj, „1" înseamnă living. */
const ORDINE = ["living", "kitchen", "bedroom", "dormitor", "hall", "hol",
                "bath", "bath2", "shower", "wc", "store", "balcony", "sas",
                "scara", "lift", "ghena", "bazin", "tech", "administrativ", "comercial"];

export interface RandLegenda {
  /** Numărul din cerc. */
  n: number;
  kind: string;
  nume: string;
  /** Câte încăperi de funcțiunea asta are nivelul. */
  bucati: number;
  /** Aria lor cumulată, în m². Zero când documentul n-o dă. */
  arie: number;
}

/** Unul sau toate planșeele cotei. O legendă se scrie pentru CE SE VEDE, iar
 *  ce se vede la «toate corpurile» sunt trei planșee, nu unul. */
export type Plansee = StoreyPlan | readonly StoreyPlan[];

const caLista = (p: Plansee): readonly StoreyPlan[] => (Array.isArray(p) ? p : [p as StoreyPlan]);

/** Legenda nivelului: funcțiunile prezente, numerotate stabil.
 *
 *  Numai ce EXISTĂ pe nivel: un rând pentru o funcțiune absentă ar fi un număr
 *  care nu se găsește nicăieri pe desen.
 *
 *  PESTE TOATE PLANȘEELE DESENATE, nu peste primul. Cu «toate corpurile» pânza
 *  arată C1, C2 și C3 deodată; o legendă scrisă doar din C1 numără o treime din
 *  camerele de pe ecran, și — mai rău — dă altă numerotare decât C2 și C3, care
 *  și-o calculează pe a lor. Atunci „2" ar însemna bucătărie pe un corp și
 *  dormitor pe cel de lângă el, pe același desen. */
export function legendaNivel(plan: Plansee): RandLegenda[] {
  const pe = new Map<string, { bucati: number; arie: number }>();
  // Spațiile ȘI părțile nucleului: scara și liftul sunt funcțiuni ale
  // nivelului la fel ca dormitorul, iar cifra din cerc de pe casa scării
  // trebuie să se găsească în legendă.
  for (const p of caLista(plan)) for (const s of [...p.spaces, ...p.cores]) {
    if (!s.kind) continue;
    const v = pe.get(s.kind) ?? { bucati: 0, arie: 0 };
    v.bucati += 1;
    v.arie += s.area ?? 0;
    pe.set(s.kind, v);
  }
  // Cele cunoscute, în ordinea fixă; apoi cele necunoscute, alfabetic — ca un
  // `kind` nou apărut în catalog să primească totuși un număr, la coadă, în loc
  // să dispară din legendă fără ca nimeni să observe.
  const cunoscute = ORDINE.filter((k) => pe.has(k));
  const restul = [...pe.keys()].filter((k) => !ORDINE.includes(k)).sort();
  return [...cunoscute, ...restul].map((kind, i) => ({
    n: i + 1,
    kind,
    nume: NUME_FUNCTIUNE[kind] ? t(NUME_FUNCTIUNE[kind]) : kind,
    bucati: pe.get(kind)!.bucati,
    arie: pe.get(kind)!.arie,
  }));
}

/** Numărul fiecărei funcțiuni, ca desenul să poată pune cifra pe cameră fără
 *  să recalculeze legenda pentru fiecare inel. */
export function numereleFunctiunilor(plan: Plansee): Map<string, number> {
  return new Map(legendaNivel(plan).map((r) => [r.kind, r.n]));
}

/** Aria utilă a nivelului: suma spațiilor măsurate.
 *
 *  Suma ARIILOR DIN DOCUMENT, nu o arie recalculată din inele — cifra din
 *  cartuș trebuie să fie aceeași cu cea pe care o scrie fiecare cameră sub
 *  numele ei, altfel planul se contrazice singur. Balcoanele se scad, fiindcă
 *  aria utilă a unui nivel nu le cuprinde. */
export function arieUtila(plan: Plansee): number {
  return caLista(plan)
    .flatMap((p) => p.spaces)
    .filter((s) => !EXTERIOARE.has(s.kind) && !s.outdoor)
    .reduce((t, s) => t + (s.area ?? 0), 0);
}

/** Spațiile deschise, care nu intră în aria utilă a nivelului: balconul și
 *  logia (amândouă `balcony`, deosebite prin `outdoor`) și terasele. */
const EXTERIOARE = new Set(["balcony", "terasa", "terasa_circulabila"]);

/** Aria balcoanelor și a logiilor — separat de cea utilă, cum se scrie în
 *  tabelul de suprafețe. */
export function arieBalcoane(plan: Plansee): number {
  return caLista(plan)
    .flatMap((p) => p.spaces)
    .filter((s) => s.kind === "balcony")
    .reduce((t, s) => t + (s.area ?? 0), 0);
}

/** Aria teraselor, separat de balcoane: o terasă a ultimului etaj nu e balcon. */
export function arieTerase(plan: Plansee): number {
  return caLista(plan)
    .flatMap((p) => p.spaces)
    .filter((s) => s.kind === "terasa" || s.kind === "terasa_circulabila")
    .reduce((t, s) => t + (s.area ?? 0), 0);
}

/** Aria unui contur de planșeu — Gauss. De afișat, ca `arieUtila`. */
function arieContur(r: readonly number[][]): number {
  let s = 0;
  for (let i = 0; i < r.length; i++) {
    const p = r[i], q = r[(i + 1) % r.length];
    s += p[0] * q[1] - q[0] * p[1];
  }
  return Math.abs(s) / 2;
}

/** REGISTRUL CIFRELOR (Cătălin, 7.10.2026: «single point of truth pe cifre»).
 *
 *  Arhitectura măsoară o dată, la scriere (`architecture/layout/cantitati.py`),
 *  și pune cifrele în `compliance.reported` — pe corp, pe etaj și pe locuință —
 *  și în `compliance.areas`, pe ansamblu. Construita e la FAȚA FINITĂ, fără
 *  terase; utila e la interior. Ecranul le CITEȘTE de acolo, ca planul de
 *  situație, arhitectura și cartușul să arate același număr. Sumele de mai jos,
 *  din spațiile desenate, rămân numai pentru un document fără registru (unul
 *  vechi, ori un import). */
export interface RandRegistru {
  built_m2?: number | null;
  footprint_m2?: number | null;
  useful_m2?: number | null;
  useful_common_m2?: number | null;
  balconies_m2?: number | null;
  loggias_m2?: number | null;
  terraces_m2?: number | null;
  flats?: number | null;
  rooms?: number | null;
}
export interface Registru {
  areas: Record<string, unknown>;
  reported: Record<string, RandRegistru>;
}

/** Registrul documentului, sau `null` când cifrele lui nu sunt măsurate pe
 *  arhitectură (estimarea planului de situație, ori un document vechi). */
export function registrulDocumentului(doc: unknown): Registru | null {
  const c = (doc as { compliance?: { areas?: Record<string, unknown>;
                                     reported?: Record<string, RandRegistru> } } | null)?.compliance;
  if (!c?.areas || c.areas.source !== "architecture" || !c.reported) return null;
  return { areas: c.areas, reported: c.reported };
}

/** Grupa de planșee DIN REGISTRU — `null` dacă vreun planșeu lipsește din el
 *  (atunci nu se amestecă: se adună tot din desen). */
function dinRegistru(plansee: readonly StoreyPlan[], reg: Registru | null | undefined): CifreGrup | null {
  if (!reg || !plansee.length) return null;
  const randuri = plansee.map((n) => reg.reported[n.id]);
  if (randuri.some((r) => !r || r.built_m2 == null)) return null;
  const s = (k: keyof RandRegistru) => randuri.reduce((t, r) => t + Number(r![k] ?? 0), 0);
  const hist = new Map<number, number>();
  for (const n of plansee) {
    for (const f of n.flatShapes ?? []) {
      const k = Number(reg.reported[f.eid]?.rooms ?? 0);
      if (k > 0) hist.set(k, (hist.get(k) ?? 0) + 1);
    }
  }
  return {
    apartamente: s("flats"),
    peCamere: [...hist.entries()].sort((a, b) => a[0] - b[0]),
    utila: s("useful_m2"),
    balcoane: s("balconies_m2") + s("loggias_m2"),
    terase: s("terraces_m2"),
    construita: s("built_m2"),
    comune: s("useful_common_m2"),
  };
}

/** Cifrele unei grupe de planșee: câte apartamente, aria utilă, aria
 *  construită (conturul planșeului, adunat). */
export interface CifreGrup {
  apartamente: number;
  /** Câte apartamente are fiecare număr de camere — `[[1, 12], [2, 30], …]`,
   *  crescător. Camere = living + dormitoare (`HABITABLE`), cum se numără un
   *  «apartament cu 2 camere». Apartamentele fără nicio cameră recunoscută
   *  (un import ale cărui spații n-au fel) nu intră: nu se știe ce sunt. */
  peCamere: [number, number][];
  /** Aria utilă FĂRĂ balcoane, logii și terase. */
  utila: number;
  /** Balcoanele și logiile, separat. */
  balcoane: number;
  terase: number;
  construita: number;
  /** Suprafața utilă comună (holuri, scări), când grupa vine din registru. */
  comune?: number;
}

function aduna(plansee: readonly StoreyPlan[], reg?: Registru | null): CifreGrup {
  const r = dinRegistru(plansee, reg);
  if (r) return r;
  const hist = new Map<number, number>();
  for (const n of plansee) {
    const camere = new Map<string, number>();
    for (const s of n.spaces) {
      if (s.flat && HABITABLE.includes(s.kind)) {
        camere.set(s.flat, (camere.get(s.flat) ?? 0) + 1);
      }
    }
    for (const f of n.flatShapes ?? []) {
      const k = camere.get(f.eid) ?? 0;
      if (k > 0) hist.set(k, (hist.get(k) ?? 0) + 1);
    }
  }
  return {
    apartamente: plansee.reduce((t, n) => t + (n.flats ?? 0), 0),
    peCamere: [...hist.entries()].sort((a, b) => a[0] - b[0]),
    utila: arieUtila(plansee),
    balcoane: arieBalcoane(plansee),
    terase: arieTerase(plansee),
    // pe PLĂCI, nu pe contur: deasupra unei bucăți oprite planșeul poate sta
    // în două bucăți, iar conturul singur e doar cea mai mare dintre ele
    construita: plansee.reduce((t, n) => t + (n.parts?.length ? n.parts : [n.outline ?? []])
      .reduce((s, r) => s + arieContur(r), 0), 0),
  };
}

/** SITUAȚIA GENERALĂ a suprafețelor (Cătălin, 6.10.2026): tabelul de bilanț al
 *  unui proiect de locuințe, pe clădire sau pe ansamblu.
 *
 *  * `suFaraTerase` — suprafața utilă a LOCUINȚELOR: camerele care țin de un
 *    apartament (`flat`), FĂRĂ balcoane, logii și terase (Cătălin, 6.10.2026:
 *    «balcoanele și logiile nu sunt din SU fără terase»);
 *  * `suTerase` — tot ce e DESCHIS: terasele, balcoanele și logiile (Cătălin,
 *    6.10.2026: «SU terase și balcoane și logii»);
 *  * `suComune` — spațiile care nu țin de niciun apartament: holurile și
 *    culoarele, casa scării, sasurile, boxele, încăperile tehnice. Puțul
 *    liftului și ghenele nu sunt suprafață utilă — intră numai în
 *    `construitComune`;
 *  * `stConstruit` — suprafața construită desfășurată (plăcile, adunate);
 *  * `construitComune` — cât din ea ocupă spațiile comune, cu lift și ghene,
 *    pentru raportul «fără spații comune».
 *  Procentele sunt `null` când numitorul e zero. */
export interface SituatieGenerala {
  suFaraTerase: number;
  suTerase: number;
  suComune: number;
  stConstruit: number;
  construitComune: number;
  /** SU (fără terase) / ST construit, %. */
  pctSuSt: number | null;
  /** SU (fără terase) / (ST construit − spațiile comune), %. */
  pctSuStFaraComune: number | null;
}

/** Puțurile: liftul și ghenele ocupă construit, dar nu sunt suprafață utilă.
 *  Orice altă parte de nucleu sau spațiu comun (scară, hol, sas, boxe,
 *  încăperile tehnice) e SU a spațiilor comune. */
const PUTURI = new Set(["lift", "ghena"]);

export function situatieGenerala(plansee: readonly StoreyPlan[],
                                 reg?: Registru | null): SituatieGenerala {
  const spatii = plansee.flatMap((n) => n.spaces);
  const suFaraTerase = spatii
    .filter((s) => s.flat && !EXTERIOARE.has(s.kind) && !s.outdoor)
    .reduce((t, s) => t + (s.area ?? 0), 0);
  const suTerase = arieTerase([...plansee]) + arieBalcoane([...plansee]);
  const comune = spatii.filter((s) => !s.flat && !EXTERIOARE.has(s.kind) && !s.outdoor);
  const comuneSpatii = comune.filter((s) => !PUTURI.has(s.kind)).reduce((t, s) => t + (s.area ?? 0), 0);
  const puturiSpatii = comune.filter((s) => PUTURI.has(s.kind)).reduce((t, s) => t + (s.area ?? 0), 0);
  const nuclee = plansee.flatMap((n) => n.cores ?? []);
  const arie = (s: { area?: number; poly: number[][] }) =>
    s.area ?? (s.poly.length >= 3 ? arieContur(s.poly) : 0);
  const nucleuUtil = nuclee.filter((c) => !PUTURI.has(c.kind)).reduce((t, c) => t + arie(c), 0);
  const nucleuPuturi = nuclee.filter((c) => PUTURI.has(c.kind)).reduce((t, c) => t + arie(c), 0);
  const pct = (a: number, b: number) => (b > 0 ? (100 * a) / b : null);
  // CU REGISTRU, cifrele sunt ale lui; din desen rămân numai puțurile (lift,
  // ghenă), care nu sunt suprafață utilă și intră doar în construitComune.
  const r = dinRegistru(plansee, reg);
  if (r) {
    const suComuneR = r.comune ?? 0;
    const construitComuneR = suComuneR + nucleuPuturi + puturiSpatii;
    const suFara = r.utila;
    return {
      suFaraTerase: suFara, suTerase: r.balcoane + r.terase, suComune: suComuneR,
      stConstruit: r.construita, construitComune: construitComuneR,
      pctSuSt: pct(suFara, r.construita),
      pctSuStFaraComune: pct(suFara, r.construita - construitComuneR),
    };
  }
  const suComune = comuneSpatii + nucleuUtil;
  const stConstruit = aduna(plansee).construita;
  const construitComune = suComune + nucleuPuturi + puturiSpatii;
  return {
    suFaraTerase, suTerase, suComune, stConstruit, construitComune,
    pctSuSt: pct(suFaraTerase, stConstruit),
    pctSuStFaraComune: pct(suFaraTerase, stConstruit - construitComune),
  };
}

export interface CifreArhitectura {
  /** Nivelul desenat — adunat peste toate planșeele lui de pe ecran: ale unui
   *  corp, sau ale tuturor corpurilor de la cota asta. */
  etaj: CifreGrup;
  /** Clădirea aleasă, cu totalul ei pe toate nivelurile — `null` la «toate
   *  corpurile». */
  cladire: (CifreGrup & { cod: string; niveluri: number }) | null;
  /** Ansamblul — numai la «toate corpurile». */
  total: (CifreGrup & { cladiri: number }) | null;
  /** SITUAȚIA GENERALĂ, pe aceeași întindere ca grupa de mai sus: clădirea
   *  aleasă, altfel ansamblul. `null` când nu e niciuna. */
  generala: (SituatieGenerala & { cod: string | null; cladiri: number }) | null;
}

/** Cifrele planului de arhitectură: pe etaj, apoi pe clădire SAU pe ansamblu.
 *
 *  `corp` e proprietarul ales (`StoreyPlan.owner`), sau `null` pentru «toate
 *  corpurile». De el depinde ce se arată, nu de ce se desenează la cotă
 *  (Stefan, 24.09.2026): la toate corpurile — etajul pe toate și complexul,
 *  fără defalcare pe clădiri; la un corp — etajul lui și corpul, fără complex.
 *
 *  Pe clădire și pe ansamblu aria construită e DESFĂȘURATĂ SUPRATERANĂ — suma
 *  planșeelor de la parter în sus —, iar aria utilă tot de acolo. Subsolul de
 *  parcaj e al ansamblului, nu al unei clădiri, și n-are apartamente; adunat
 *  la aria utilă ar fi pus sub «apartamente» aria culoarelor de la −2. */
export function cifreArhitectura(niveluri: readonly StoreyPlan[],
                                 desenate: readonly StoreyPlan[],
                                 corp: string | null,
                                 registru?: Registru | null): CifreArhitectura {
  const supra = niveluri.filter((n) => n.level >= 0 && !n.parcare);
  const peCorp = new Map<string, StoreyPlan[]>();
  for (const n of supra) {
    const k = n.owner ?? "";
    if (!peCorp.has(k)) peCorp.set(k, []);
    peCorp.get(k)!.push(n);
  }
  const cuApartamente = [...peCorp.values()]
    .filter((ns) => ns.some((n) => (n.flats ?? 0) > 0));
  const ale = corp !== null ? peCorp.get(corp) : undefined;
  const generala = ale?.length
    ? { ...situatieGenerala(ale, registru), cod: ale[0].cod ?? "", cladiri: 1 }
    : corp === null && cuApartamente.length
      ? { ...situatieGenerala(cuApartamente.flat(), registru), cod: null, cladiri: cuApartamente.length }
      : null;
  return {
    generala,
    etaj: aduna(desenate, registru),
    cladire: ale?.length
      ? { ...aduna(ale, registru), cod: ale[0].cod ?? "", niveluri: ale.length } : null,
    total: corp === null && cuApartamente.length
      ? { ...aduna(cuApartamente.flat(), registru), cladiri: cuApartamente.length } : null,
  };
}
